import { useWindowHeight } from '@react-hook/window-size';
import { Button, CheckboxOptionType, Radio } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import customMessage from '@/components/Message';
import Bar from '@/components/Nivo/Bar';
import { TabContainer, SelectorContainer, Label, Container, ChartOptions, ChartTypeContainer } from '@/components/Styled';
import CONFIG, { modules } from '@/config';
import { getCurrentChartData } from '@/services/current-data';
import { getSamplesChartData } from '@/services/sample';
import { useAppSelector } from '@/store/hooks';
import { ChartType, SamplesCategoriesType } from '@/types/sample';

interface Props {
  chartHeight?: number;
  currentData?: boolean;
}

const chartValues = [
  { label: 'Sztuki', value: 'items' },
  { label: 'Procenty', value: 'percent' }
];

const chartType = [
  { label: 'Stos', value: 'stacked' },
  { label: 'Grupy', value: 'grouped' }
];

const Charts = ({ chartHeight, currentData }: Props): JSX.Element => {
  const { selectedFields, date } = useAppSelector(({ samplesState }) => samplesState);

  const [filterEmpty, setFilterEmpty] = useState(true);
  const [data, setData] = useState<ChartType>();

  const [selectedChart, setSelectedChart] = useState('');
  const [selectedChartValues, setSelectedChartValues] = useState('percent');
  const [selectedChartType, setSelectedChartType] = useState('grouped');

  const allOptionsSelected = useMemo(() => !Object.keys(selectedFields).length, [selectedFields]);

  const filteredData = useMemo(() => (
    data
      ? {
        grouped: data.grouped.filter((element) => element[selectedChart] !== '-' && element.all_items > 20),
        stacked: data.stacked.filter((element) => element[selectedChart] !== '-' && element.all_items > 20)
      }
      : {
        grouped: [],
        stacked: []
      }
  ), [data, selectedChart]);

  const options = useMemo(() => [
    (selectedFields.gname || allOptionsSelected)
      ? { label: 'Nazwa partii', value: 'gname' }
      : {},
    (selectedFields.grm || allOptionsSelected)
      ? { label: 'GRM', value: 'grm' }
      : {},
    (selectedFields.lot || allOptionsSelected)
      ? { label: 'LOT', value: 'lot' }
      : {},
    (selectedFields.station || allOptionsSelected)
      ? { label: 'Station', value: 'station' }
      : {},
    modules.numpalet && (selectedFields.numpalet || allOptionsSelected)
      ? { label: 'Nr palety', value: 'numpalet' }
      : {},
    modules.extnumord && (selectedFields.extnumord || allOptionsSelected)
      ? { label: 'Extnumord', value: 'extnumord' }
      : {},
    modules.batch && (selectedFields.batch || allOptionsSelected)
      ? { label: 'Batch', value: 'batch' }
      : {}
  ].filter((({ value }) => value)) as CheckboxOptionType[], [selectedFields, allOptionsSelected]);

  const height = useWindowHeight();

  useEffect(() => {
    setSelectedChart(String(options[0].value));
  }, [selectedFields, date]);

  useEffect(() => {
    fetchChartData();
    setFilterEmpty(true);
    let interval: NodeJS.Timer;
    if (currentData) {
      interval = setInterval(() => {
        fetchChartData();
      }, CONFIG.refreshTime);
    }
    return () => clearInterval(interval);
  }, [selectedChart, date]);

  const fetchChartData = async (): Promise<void> => {
    setData(undefined);
    if (selectedChart !== '') {
      if (currentData) {
        try {
          const response = await getCurrentChartData(
            date.start,
            date.end,
            selectedChart,
            selectedFields[selectedChart as keyof SamplesCategoriesType]
          );
          setData(response);
        } catch (error) {
          console.error((error as Error).message);
        }
      } else {
        try {
          const response = await getSamplesChartData(
            date.start,
            date.end,
            selectedChart,
            selectedFields[selectedChart as keyof SamplesCategoriesType]
          );
          setData(response);
        } catch (error) {
          console.error((error as Error).message);
        }
      }
    }
  };

  const checkEmpty = (): void => {
    if (data?.stacked.length === filteredData.stacked.length) {
      customMessage('Brak dodatkowych słupków do wyświetlenia!', 'warning', 5);
    } else {
      setFilterEmpty((prev) => !prev);
    }
  };

  return (
    <TabContainer>
      <SelectorContainer>
        <div>
          <Label>
            Wybrany wykres
          </Label>
          <Radio.Group
            onChange={({ target }): void => setSelectedChart(target.value)}
            options={options}
            value={selectedChart}
          />
        </div>
        <ChartOptions>
          <Button
            onClick={checkEmpty}
            onFocus={(e): React.FocusEvent<Element> => e.target.blur()}
            style={{ width: 200 }}
          >
            { filterEmpty ? 'Wyświetl wszystkie słupki' : 'Ukryj nieistotne słupki' }
          </Button>
          <ChartTypeContainer>
            <div>
              <Label>
                Typ wykresu
              </Label>
              <Radio.Group
                onChange={({ target }): void => setSelectedChartType(target.value)}
                options={chartType}
                value={selectedChartType}
              />
            </div>
            <div>
              <Label>
                Wyświetlane jednostki
              </Label>
              <Radio.Group
                onChange={({ target }): void => setSelectedChartValues(target.value)}
                options={chartValues}
                value={selectedChartValues}
              />
            </div>
          </ChartTypeContainer>
        </ChartOptions>
      </SelectorContainer>
      <Container style={{ height: chartHeight || height - 264, minHeight: 560 }}>
        { data && filteredData && (
          <Bar
            chartData={filterEmpty ? filteredData : data}
            chartKey={selectedChart}
            chartType={selectedChartType}
            chartValues={selectedChartValues}
          />
        ) }
      </Container>
    </TabContainer>
  );
};

export default Charts;
